import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import {
	MAX_COLUMNS,
	REORDER_COLUMNS,
} from '@atlassian/jira-native-issue-table/src/common/constants.tsx';
import type { ColumnConfigChangeType } from '@atlassian/jira-native-issue-table/src/common/types.tsx';
import type { Props as NativeIssueTableProps } from '@atlassian/jira-native-issue-table/src/ui/types.tsx';
import type {
	useColumnsChangeMutation as UseColumnsChangeMutation,
	useColumnsChangeMutation$data as UseColumnsChangeMutationData,
} from '@atlassian/jira-relay/src/__generated__/useColumnsChangeMutation.graphql';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { onColumnsChangeErrorFlag } from '../../common/flags/index.tsx';
import { useColumnsUpdater } from '../use-colums-updater/index.tsx';

// TODO: Use `ColumnId` from common types - https://jplat.atlassian.net/browse/JIV-20496
type ColumnId = string;

interface Props {
	fieldSetViewId: string | null;
	/** __ID of the `JiraIssueSearchFieldSetConnection` record in the JiraFieldSetView */
	fieldSetsConnectionId: string;
	onCompleted?: (
		changeType: ColumnConfigChangeType,
		success: boolean | undefined,
		columnIds: ColumnId[],
	) => void;
	onError?: (error: Error) => void;
}

export const useColumnsChange = ({
	fieldSetViewId,
	fieldSetsConnectionId,
	onCompleted,
	onError,
}: Props) => {
	const { showFlag } = useFlagsService();
	const issueKey = useIssueKey();

	const [replaceListViewFieldSets, isInFlight] = useMutation<UseColumnsChangeMutation>(graphql`
		mutation useColumnsChangeMutation(
			$id: ID!
			$fieldSetsInput: JiraFieldSetsMutationInput
			$amountOfColumns: Int!
			$includeView: Boolean!
		) @raw_response_type {
			jira {
				updateFieldSetsView(id: $id, fieldSetsInput: $fieldSetsInput) {
					success
					view @include(if: $includeView) {
						id
						hasDefaultFieldSets
						...ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_fieldSetViewQueryByProject
							@arguments(amountOfColumns: $amountOfColumns)
					}
				}
			}
		}
	`);

	/**
	 * When the the mutation is unsuccessful then return an error flag and fire off error analytics
	 * otherwise modify the columns with the new fieldset
	 */
	const replaceIssueSearchViewFieldSetOnComplete = useCallback(
		(changeType: ColumnConfigChangeType, success: boolean | undefined, columnIds: ColumnId[]) => {
			onCompleted?.(changeType, success, columnIds);
			if (!success) {
				showFlag(onColumnsChangeErrorFlag);
				fireErrorAnalytics({
					meta: {
						id: 'commitReplaceJiraIssueCommonViewsFieldSetsMutationUnsuccessful',
						packageName: 'jiraIssueViewCommonViews',
						teamName: 'bento',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		},
		[onCompleted, showFlag],
	);

	/**
	 * When the the mutation is unsuccessful then return an error flag and fire off error analytics
	 */
	const replaceIssueSearchViewFieldSetOnError = useCallback(
		(error: Error) => {
			onError?.(error);
			showFlag(onColumnsChangeErrorFlag);
			fireErrorAnalytics({
				meta: {
					id: 'commitReplaceJiraIssueCommonViewsSetsMutationError',
					packageName: 'jiraIssueViewCommonViews',
					teamName: 'bento',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		[onError, showFlag],
	);

	const updateColumns = useColumnsUpdater(fieldSetsConnectionId);

	/**
	 * onColumnsChange is invoked when there is a change in the list of visible columns.
	 * For optimistic updates the optimisticUpdater gets called
	 * and we see the expected change then mutation happens, optimisticUpdater gets
	 * reverted as a result of mutation then updater gets called to persist the optimistic update view.
	 */
	const onColumnsChange = useCallback<NonNullable<NativeIssueTableProps['onColumnsChange']>>(
		(columnIds, changeType, optimisticFieldSets) => {
			const context = {
				issueContext: {
					issueKey,
				},
			};

			replaceListViewFieldSets({
				variables: {
					// the id passed into the hook is a dummy id
					// so we need to hand craft our own ID for now
					id: fieldSetViewId || '',
					amountOfColumns: MAX_COLUMNS,
					includeView: changeType === REORDER_COLUMNS,
					fieldSetsInput: {
						replaceFieldSetsInput: {
							nodes: columnIds,
							context,
						},
					},
				},
				optimisticUpdater(store) {
					updateColumns(store, changeType, optimisticFieldSets);
				},
				updater(store) {
					updateColumns(store, changeType, optimisticFieldSets);
				},
				onCompleted(columnData: UseColumnsChangeMutationData) {
					replaceIssueSearchViewFieldSetOnComplete(
						changeType,
						columnData.jira?.updateFieldSetsView?.success,
						columnIds,
					);
				},
				onError(error: Error) {
					replaceIssueSearchViewFieldSetOnError(error);
				},
			});
		},
		[
			issueKey,
			replaceIssueSearchViewFieldSetOnComplete,
			replaceIssueSearchViewFieldSetOnError,
			replaceListViewFieldSets,
			updateColumns,
			fieldSetViewId,
		],
	);

	return [onColumnsChange, isInFlight] as const;
};
